import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Lock, 
  User, 
  Key, 
  Shield, 
  Eye, 
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context helper
export const setAdminToken = (token) => {
  localStorage.setItem("admin_token", token);
};

export const getAdminToken = () => {
  return localStorage.getItem("admin_token");
};

export const clearAdminToken = () => {
  localStorage.removeItem("admin_token");
};

export const isAdminLoggedIn = () => {
  return !!getAdminToken();
};

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(null); // null = loading, true = needs login, false = needs setup
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    totp_code: ""
  });

  useEffect(() => {
    checkSetup();
  }, []);

  const checkSetup = async () => {
    try {
      const res = await axios.get(`${API}/admin/check-setup`);
      setIsSetup(res.data.is_setup);
    } catch (error) {
      console.error("Error checking setup:", error);
      setIsSetup(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/admin/setup`, {
        username: formData.username,
        password: formData.password
      });
      
      setQrCode(res.data.qr_code);
      setTotpSecret(res.data.totp_secret);
      setSetupComplete(true);
      toast.success("Admin account created! Scan QR code with Google Authenticator.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, formData);
      setAdminToken(res.data.token);
      toast.success(`Welcome back, ${res.data.username}!`);
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Loading state
  if (isSetup === null) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#0A3F2F]" />
      </div>
    );
  }

  // Setup Complete - Show QR Code
  if (setupComplete && qrCode) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
        <motion.div
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F]">Setup Complete!</h1>
            <p className="text-[#4A5F55] mt-2">Scan this QR code with Google Authenticator</p>
          </div>

          <div className="bg-[#F5F5F0] rounded-xl p-4 mb-6 flex justify-center">
            <img src={qrCode} alt="QR Code for Google Authenticator" className="w-48 h-48" />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-yellow-800 font-medium mb-2">Backup Code (save this!):</p>
            <code className="text-xs bg-yellow-100 px-2 py-1 rounded font-mono break-all">{totpSecret}</code>
          </div>

          <button
            onClick={() => { setSetupComplete(false); setIsSetup(true); }}
            className="w-full bg-[#0A3F2F] text-white py-3 rounded-full font-semibold hover:bg-[#0d5139] transition-colors"
            data-testid="proceed-to-login"
          >
            Proceed to Login
          </button>
        </motion.div>
      </div>
    );
  }

  // Setup Form (first time)
  if (!isSetup) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
        <motion.div
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#0A3F2F] flex items-center justify-center mx-auto mb-4">
              <Shield size={32} className="text-[#D4FF33]" />
            </div>
            <h1 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F]">Admin Setup</h1>
            <p className="text-[#4A5F55] mt-2">Create your admin account with 2FA protection</p>
          </div>

          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Username</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  minLength={3}
                  placeholder="Choose a username"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                  data-testid="setup-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  placeholder="Min 8 characters"
                  className="w-full pl-10 pr-12 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                  data-testid="setup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5F55]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  After setup, you'll need to scan a QR code with Google Authenticator app to enable 2FA.
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A3F2F] text-white py-3 rounded-full font-semibold hover:bg-[#0d5139] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-6"
              data-testid="setup-submit"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <Shield size={20} />}
              {loading ? "Creating..." : "Create Admin Account"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Login Form
  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#0A3F2F] flex items-center justify-center mx-auto mb-4">
            <Lock size={32} className="text-[#D4FF33]" />
          </div>
          <h1 className="font-['Syne'] text-2xl font-bold text-[#0A3F2F]">Admin Login</h1>
          <p className="text-[#4A5F55] mt-2">Enter your credentials and authenticator code</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Username</label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                placeholder="Your username"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                data-testid="login-username"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Your password"
                className="w-full pl-10 pr-12 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5F55]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Authenticator Code</label>
            <div className="relative">
              <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
              <input
                type="text"
                name="totp_code"
                value={formData.totp_code}
                onChange={handleChange}
                required
                maxLength={6}
                placeholder="6-digit code"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] font-mono text-center text-lg tracking-widest"
                data-testid="login-totp"
              />
            </div>
            <p className="text-xs text-[#4A5F55] mt-1">Open Google Authenticator for the code</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A3F2F] text-white py-3 rounded-full font-semibold hover:bg-[#0d5139] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-6"
            data-testid="login-submit"
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <Lock size={20} />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-[#4A5F55] hover:text-[#0A3F2F]">
            ← Back to website
          </a>
        </div>
      </motion.div>
    </div>
  );
}
